/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
// libraries
import { useDebounce } from 'use-lodash-debounce';
import { Chip, Box, Paper, Divider, Popover, Typography } from '@mui/material';
import Iconify from '../Iconify';
// components
import { RHFAutocomplete, RHFCheckbox } from '../hook-form';
// redux
import { dispatch, useSelector } from '../../redux/store';
import { getPracticeTreatmentList, startLoading } from '../../redux/slices/practiceTreatment';
// utils
import groupedTreatments from '../../utils/groupedTreatments';
// style
import { treatmentChipTextStyle, treatmentChipBoxStyle, filterDropdownPaperStyle } from '../../styles/NewLead';

// Sentinel id used to signal "no treatment" — never a real treatment id.
export const NO_TREATMENT_SENTINEL_ID = '__NO_TREATMENT__';

const MultiTreatmentDropdown = ({ type, name, value, onChange, noTreatmentSelected, selfFetchLimit, ...props }) => {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { treatmentsList: { results, count }, isLoading } = useSelector((state) => state.practiceTreatment);
    const { treatmentStarted: { categoryName } } = useSelector((state) => state.lead);
    const { newCampaign, selectedCampaign } = useSelector((state) => state.campaign);
    const [treatmentOptions, setTreatmentOptions] = useState([]);
    const [selectedTreatment, setSelectedTreatment] = useState([]);
    const [position, setPosition] = useState(0);
    const [inputValue, setInputValue] = useState('');
    const [selectAll, setSelectAll] = useState(false);
    const [shouldCall, setShouldCall] = useState(false);
    const inputValueDebounce = useDebounce(inputValue, 800);
    const listElem = useRef();
    const mounted = useRef();
    // Tracks the practiceId we've already issued a self-fetch for, so the empty-list
    // recovery below fires at most once per practice (no refetch loop when a practice
    // genuinely has zero treatments).
    const selfFetchedForPractice = useRef(null);

    // SPA-navigation hydration guard: on a fresh full-page load a parent dispatches the
    // treatment fetch, but on client-side (sidebar) navigation that dispatch can be
    // skipped, leaving treatmentsList empty so the dropdown renders "No options". When we
    // mount with an empty list and nothing in flight, fetch the options ourselves once.
    //
    // IMPORTANT: this fetch's `limit` must byte-match whatever limit the co-mounting
    // page's OWN treatment fetch uses, or callApi's in-flight dedupe (URL-keyed) can't
    // collapse the two into one network request and every load doubles up. Most hosts
    // (autoReminder, compaign, NHSLead, etc.) already fetch their own copy at limit:30,
    // which is why `selfFetchLimit` defaults to 30 — changing the default would silently
    // reintroduce a duplicate-fetch on every one of those pages. A host passes an explicit
    // `selfFetchLimit` override only when ITS OWN fetch uses a different limit (currently
    // just NewLead.jsx's `type='NewLeadTable'` render, which fetches its own copy at
    // limit:50 via NewLead.jsx's practiceTreatment effect).
    //
    // Deliberately NOT gated on the `type` prop here: `type` is a free-text label chosen
    // per host and is NOT unique across host components — e.g. NHSLeadTable.jsx also hands
    // NHSLead.jsx (a different host, whose own fetch is limit:30) `type: 'NewLeadTable'`.
    // Branching on `type === 'NewLeadTable'` inside this shared component would have fired
    // limit:50 for that host too and broken its dedupe. The caller-supplied prop sidesteps
    // that collision entirely.
    useEffect(() => {
        if (!practiceId) return;
        if (isLoading) return;
        if (results && results.length > 0) return;
        if (selfFetchedForPractice.current === practiceId) return;
        selfFetchedForPractice.current = practiceId;
        dispatch(getPracticeTreatmentList(practiceId, {
            limit: selfFetchLimit,
            offset: 0,
            type: 'searchTreatment',
            category: type === 'treatmentStarted' ? categoryName !== 'All' && categoryName : null,
        }));
    }, [practiceId, results, isLoading]);

    useEffect(() => {
        if (results) {
            setTreatmentOptions(groupedTreatments(results));
        }
        if (type === 'autoReminder' || type === 'compaign') {
            if (results?.length < count) {
                const data = {
                    limit: count,
                    offset: results.length
                };
                dispatch(getPracticeTreatmentList(practiceId, data));
            }
        }
    }, [results]);


    useEffect(() => {
        if (Object.values(selectedCampaign).length > 0) {
            const data = selectedCampaign?.recepient?.filters?.lead_treatments__treatment__treatment_item__name__in;
            const matchingFilms = treatmentOptions?.filter(film => data?.includes(film?.name));
            if (data !== undefined && data !== null && inputValueDebounce === '') {
                setSelectedTreatment(matchingFilms);
            }
        }
    }, [selectedCampaign, treatmentOptions]);

    const loadMoreData = () => {
        if (count > results?.length) {
            const data = {
                limit: 30,
                offset: results?.length
            };
            dispatch(getPracticeTreatmentList(practiceId, data));
        }
    };

    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
        } else if (position && listElem.current) {
            listElem.current.scrollTop = position - listElem?.current?.offsetHeight;
        }
    }, [position]);

    // ISS-044 — landing seeds write id-only stubs, which render as EMPTY chips.
    //
    // Callers that pre-select treatments from practice preferences
    // (default_filters[...].treatment_ids) build their value objects as
    // `ids.map((id) => ({ id }))` — see ConsultantAttend.jsx's landing seed and
    // useDefaultTreatmentFilter.js. Those objects carry NO `name`, and this component
    // renders every label from `option.name` (getOptionLabel, renderOption, and
    // SelectedTreatmentChips below), so the user got a chip containing only its remove
    // button. `isOptionEqualToValue` also compared on `name`, so a stub could never
    // match its option and the listbox row stayed unchecked.
    //
    // Hydrating here rather than at each seed site fixes every board at once
    // (Attended, Booked, Started, New, Closed, Archived) and is resilient to ordering:
    // the seed runs before the treatment list has loaded, so a seed-time lookup would
    // find nothing. This re-runs whenever the options arrive.
    const hydratedValue = useMemo(() => {
        if (!Array.isArray(value) || value.length === 0) return value;
        if (!treatmentOptions?.length) return value;
        return value.map((item) => {
            if (!item || item.name || item.id == null) return item;
            const match = treatmentOptions.find((opt) => String(opt?.id) === String(item.id));
            return match || item;
        });
    }, [value, treatmentOptions]);

    const handleDelete = (delValue) => {
        const tempData = type === 'compaign' ? (newCampaign.treatmentName || selectedTreatment || []) : value;
        const data = tempData?.filter((res) => res.id !== delValue);
        onChange('handleDelete', data, selectedCampaign);
    };

    useEffect(() => {
        if (practiceId && shouldCall) {
            setShouldCall(false);
            if (inputValueDebounce) {
                const data = {
                    type: 'searchTreatment',
                    treatment: inputValueDebounce,
                    category: type === 'treatmentStarted' ? categoryName !== 'All' && categoryName : null
                };
                dispatch(getPracticeTreatmentList(practiceId, data));
            } else {
                const data = {
                    limit: 30,
                    offset: 0,
                    type: 'searchTreatment',
                    category: type === 'treatmentStarted' ? categoryName !== 'All' && categoryName : null
                };
                dispatch(getPracticeTreatmentList(practiceId, data));
            }
        }
    }, [inputValueDebounce]);

    const inputChangeHandler = (value) => {
        dispatch(startLoading());
        setShouldCall(true);
        setInputValue(value);
    };

    const handleNoTreatmentClick = (e) => {
        e.preventDefault();
        // Signal "no treatment" via the sentinel — clear real treatment selections
        onChange(e, [{ id: NO_TREATMENT_SENTINEL_ID, name: 'No treatment', isNoTreatmentSentinel: true }], selectedCampaign);
    };

    const handleToggleSelectAll = (e) => {
        e.preventDefault();
        setSelectAll(!selectAll);
        const data = {
            limit: !selectAll ? count : 30,
            offset: 0
        };
        dispatch(getPracticeTreatmentList(practiceId, data));
        if (!selectAll) {
            onChange('', treatmentOptions, selectedCampaign);
            return;
        }
        onChange('', [], selectedCampaign);
    };

    const handleOnChanged = (e, value) => {
        onChange(e, value, selectedCampaign);
        if (value.length === 0 || value?.length !== count) {
            setSelectAll(false);
        }
        if (value?.length === count) {
            setSelectAll(true);
        }
    };

    const handleScroll = (event) => {
        const listboxNode = event.target.scrollHeight <= event.target.scrollTop + event.target.clientHeight;
        if (listboxNode) {
            setPosition(event.target.scrollTop + event.target.clientHeight);
            loadMoreData();
        }
    };

    return (
        <RHFAutocomplete
            {...props}
            multiple
            name={name}
            value={type === 'compaign' ? (newCampaign.treatmentName || selectedTreatment || []) : hydratedValue}
            onChange={handleOnChanged}
            onInputChange={(e, newValue) => inputChangeHandler(newValue)}
            options={treatmentOptions}
            noOptionsText={isLoading ? 'Loading...' : 'No options'}
            groupBy={(option) => option?.firstLetter}
            isOptionEqualToValue={(option, value) => (
                option?.id != null && value?.id != null
                    ? String(option.id) === String(value.id)
                    : option?.name === value?.name
            )}
            getOptionLabel={(option) => option?.name}
            renderTags={(value) => (
                <SelectedTreatmentChips value={value} type={type} onDelete={handleDelete} />
            )}
            renderOption={(props, option, { selected }) => (
                <li {...props} key={option.name} style={{ padding: '5px 9px' }}>
                    <Box sx={{ padding: '0px 12px' }} >
                        <RHFCheckbox renderSelect checked={selected} label={option.name} name={option.name} />
                    </Box>
                </li>
            )}
            ListboxProps={{
                ref: listElem,
                onScroll: handleScroll
            }}
            PaperComponent={(paperProps) => {
                const { children, ...restPaperProps } = paperProps;
                return (
                    <Paper {...restPaperProps} sx={{ ...restPaperProps.sx, ...filterDropdownPaperStyle }}>
                        {type === 'NewLeadTable' && (
                            <>
                                <Box
                                    onMouseDown={handleNoTreatmentClick}
                                    sx={{ padding: '5px 12px', ':hover': { cursor: 'pointer', bgcolor: 'action.hover' } }}
                                >
                                    <RHFCheckbox
                                        id='no-treatment-checkbox'
                                        checked={!!noTreatmentSelected}
                                        name='No treatment'
                                        label='No treatment'
                                        onClick={handleNoTreatmentClick}
                                    />
                                </Box>
                                <Divider />
                            </>
                        )}
                        {(type === 'autoReminder') &&
                            <>
                                <Box
                                    onMouseDown={(e) => e.preventDefault()}
                                    sx={{ padding: '5px 12px', ':hover': { cursor: 'pointer' } }}
                                >
                                    <RHFCheckbox
                                        id='select-all-checkbox'
                                        checked={selectAll}
                                        name='Select All'
                                        label='Select All'
                                        onClick={handleToggleSelectAll}
                                    />
                                </Box>
                                <Divider />
                            </>}
                        {children}
                    </Paper>
                );
            }}
        />
    );
};

MultiTreatmentDropdown.propTypes = {
    name: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.any,
    onChange: PropTypes.func,
    control: PropTypes.any,
    noTreatmentSelected: PropTypes.bool,
    // Limit used by the SPA-navigation hydration guard's self-fetch. Must match the
    // co-mounting host page's own treatment-fetch limit — see the effect comment above.
    selfFetchLimit: PropTypes.number,
};

MultiTreatmentDropdown.defaultProps = {
    noTreatmentSelected: false,
    selfFetchLimit: 30,
};

export default MultiTreatmentDropdown;

const SelectedTreatmentChips = ({ value = [], onDelete, type }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const visibleChips = value.slice(0, 2);

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    return (
        <>
            <Box sx={treatmentChipBoxStyle}>
                {value.length === 0 && (
                    <Typography variant='body2' color='text.disabled'>
                        No treatments selected
                    </Typography>
                )}
                {value.length === 1 && visibleChips.map((item, index) => (
                    <Chip
                        key={index}
                        sx={treatmentChipTextStyle(type)}
                        label={item?.name}
                        size='small'
                        color='info'
                        variant='soft'
                        onDelete={() => onDelete(item.id)}
                    />
                ))}
                {value.length > 1 && (
                    <Chip
                        sx={treatmentChipTextStyle(type)}
                        color='info'
                        variant='soft'
                        onClick={handleOpen}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.5, fontSize: 'inherit' }}>
                                <Box component='span'>{value.length}</Box>
                                <Box component='span'>selected</Box>
                                <Iconify icon='eva:external-link-outline' width={16} height={16} sx={{ color: 'inherit' }} />
                            </Box>
                        }
                    />
                )}
            </Box>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { p: 2, maxWidth: 320 } }}
            >
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Selected treatments
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 260, overflowY: 'auto' }}>
                    {value.length === 0 && (
                        <Typography variant='body2' color='text.disabled'>
                            None selected yet
                        </Typography>
                    )}
                    {value.map((item, index) => (
                        <Chip
                            key={index}
                            sx={treatmentChipTextStyle(type)}
                            label={item?.name}
                            size='small'
                            color='info'
                            variant='soft'
                            onDelete={() => {
                                onDelete(item.id);
                                if (value.length === 1) {
                                    handleClose();
                                }
                            }}
                        />
                    ))}
                </Box>
            </Popover>
        </>
    );
};

SelectedTreatmentChips.propTypes = {
    value: PropTypes.array,
    type: PropTypes.string,
    onDelete: PropTypes.func,
};
